export interface LoginResult {
  success: boolean;
  sessionToken?: string;
  accountId?: string;
  error?: string;
}

export interface RegisterResult {
  success: boolean;
  accountId?: string;
  error?: string;
}

export interface CharacterSummary {
  id: string;
  name: string;
  level: number;
  class: string;
  race?: string;
  sex?: string;
  headId?: number;
  /** ID del mapa donde está el personaje */
  mapId?: number;
  /** Coordenada X en el mapa */
  x?: number;
  /** Coordenada Y en el mapa */
  y?: number;
  // ---- Visuales del aspecto (para dibujar el personaje vestido en selección/creación) ----
  /** Body a dibujar: armadura equipada si hay, sino el naked body de raza/sexo. */
  bodyId?: number;
  /** Naked body de raza/sexo (fallback/base del body). */
  nakedBodyId?: number;
  /** ID visual del arma equipada (0 = ninguna). */
  weaponId?: number;
  /** ID visual del escudo equipado (0 = ninguno). */
  shieldId?: number;
  /** ID visual del casco equipado (0 = ninguno). */
  helmId?: number;
  /** ID visual de las botas equipadas (0 = ninguna). */
  bootId?: number;
}

export interface CreateCharacterResult {
  success: boolean;
  characterId?: string;
  error?: string;
}

export interface DeleteCharacterResult {
  success: boolean;
  error?: string;
}

export interface LogoutResult {
  success: boolean;
  error?: string;
}

export class AuthApi {
  private readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/+$/, '');
  }

  private get authUrl(): string {
    return `${this.baseUrl}/api/auth`;
  }

  async register(username: string, email: string, password: string): Promise<RegisterResult> {
    const res = await fetch(`${this.authUrl}/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, email, password }),
    });

    if (!res.ok) {
      const data = (await res.json()) as { error?: string };
      return { success: false, error: data.error ?? 'Register failed' };
    }

    const data = (await res.json()) as { accountId: string };
    return { success: true, accountId: data.accountId };
  }

  async login(username: string, password: string): Promise<LoginResult> {
    const res = await fetch(`${this.authUrl}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    const data = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      sessionToken?: string;
      accountId?: string;
      error?: string;
    };

    if (!res.ok || !data.success || !data.sessionToken) {
      return { success: false, error: data.error ?? 'Login failed' };
    }

    return {
      success: true,
      sessionToken: data.sessionToken,
      accountId: data.accountId,
    };
  }

  /**
   * Login con Discord (OAuth2) via popup + postMessage.
   *
   * Abre `${baseUrl}/api/auth/discord` en una ventana popup. El server maneja el
   * flujo con Discord y, al terminar, sirve una página que hace
   * `postMessage({ type:'aureum-oauth', sessionToken })` a esta ventana y se cierra.
   * Acá validamos el origen del mensaje y resolvemos con el token. Preserva el warmup
   * del juego (no recarga la página). Ver docs/AUTH_AND_LOGIN.md.
   */
  async loginWithDiscordPopup(): Promise<LoginResult> {
    const expectedOrigin = new URL(this.baseUrl).origin;
    const popup = window.open(
      `${this.baseUrl}/api/auth/discord`,
      'aureum-discord-oauth',
      'width=500,height=750,menubar=no,toolbar=no'
    );

    if (!popup) {
      return { success: false, error: 'No se pudo abrir la ventana de Discord (¿popup bloqueado?).' };
    }

    return new Promise<LoginResult>((resolve) => {
      let settled = false;

      const cleanup = (): void => {
        window.removeEventListener('message', onMessage);
        window.clearInterval(closedTimer);
        window.clearTimeout(timeout);
      };

      const finish = (result: LoginResult): void => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(result);
      };

      const onMessage = (event: MessageEvent): void => {
        // Solo aceptamos mensajes del origen del server (el callback OAuth).
        if (event.origin !== expectedOrigin) return;
        const data = event.data as { type?: string; sessionToken?: string; error?: string };
        if (!data || data.type !== 'aureum-oauth') return;

        if (data.sessionToken) {
          finish({ success: true, sessionToken: data.sessionToken });
        } else {
          finish({ success: false, error: data.error ?? 'Error de login con Discord' });
        }
      };

      window.addEventListener('message', onMessage);

      // Si el usuario cierra el popup sin completar, resolvemos como cancelado.
      const closedTimer = window.setInterval(() => {
        if (popup.closed) {
          finish({ success: false, error: 'Login con Discord cancelado.' });
        }
      }, 500);

      // Red de seguridad: no dejar la promesa colgada para siempre.
      const timeout = window.setTimeout(() => {
        try {
          popup.close();
        } catch {
          // ignore
        }
        finish({ success: false, error: 'Tiempo de espera agotado con Discord.' });
      }, 3 * 60 * 1000);
    });
  }

  async logout(sessionToken: string): Promise<LogoutResult> {
    const res = await fetch(`${this.authUrl}/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionToken}`,
      },
    });

    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      return { success: false, error: data.error ?? 'Logout failed' };
    }

    return { success: true };
  }

  async getCharacters(sessionToken: string): Promise<CharacterSummary[]> {
    const res = await fetch(`${this.authUrl}/characters`, {
      headers: { Authorization: `Bearer ${sessionToken}` },
    });

    if (!res.ok) {
      return [];
    }

    const data = (await res.json()) as { characters: CharacterSummary[] };
    return data.characters ?? [];
  }

  async createCharacter(
    sessionToken: string,
    name: string,
    characterClass: string,
    race?: string,
    sex?: string,
    headId?: number
  ): Promise<CreateCharacterResult> {
    const res = await fetch(`${this.authUrl}/characters`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionToken}`,
      },
      body: JSON.stringify({ name, class: characterClass, race, sex, headId }),
    });

    const data = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      characterId?: string;
      error?: string;
    };

    if (!res.ok || !data.success || !data.characterId) {
      return { success: false, error: data.error ?? 'Create character failed' };
    }

    return { success: true, characterId: data.characterId };
  }

  async deleteCharacter(
    sessionToken: string,
    characterId: string
  ): Promise<DeleteCharacterResult> {
    const res = await fetch(`${this.authUrl}/characters/${encodeURIComponent(characterId)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${sessionToken}` },
    });

    const data = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      error?: string;
    };

    if (!res.ok || !data.success) {
      return { success: false, error: data.error ?? 'Delete character failed' };
    }

    return { success: true };
  }
}


